import { MicVAD } from '@ricky0123/vad-web';
import type { Settings } from '@voicer/shared';
import { concatFloat32, rms } from './wav';

export interface MicEvents {
  onSpeechStart: (ts: number) => void;
  onSpeechEnd: (audio: Float32Array, startTs: number, endTs: number) => void;
  onMisfire: () => void;
  onFrame: (prob: number, micRms: number, frame: Float32Array) => void;
}

const SAMPLE_RATE = 16000;
/** Keep enough ring history for the longest pad we allow in settings. */
const RING_CAP_MS = 1500;

/**
 * Microphone capture with Silero VAD v5 (AudioWorklet). In duplex mode the VAD drives
 * speech start/end; in push-to-talk mode frames are collected while the key is held.
 *
 * A rolling frame ring is snapshotted at speech onset so Whisper always gets lead-in audio
 * even when the worklet pad is short or setOptions lags.
 */
export class Mic {
  private vad: MicVAD | null = null;
  private ptt = false;
  private pttFrames: Float32Array[] = [];
  private pttStart = 0;
  private speechStartTs = 0;
  private frameRing: Float32Array[] = [];
  private ringSamples = 0;
  /** Pre-onset audio captured at speechStart (before the ring fills with the utterance). */
  private leadSnapshot: Float32Array | null = null;
  private padMs = 800;
  mode: 'duplex' | 'ptt' | 'off' = 'off';
  ready = false;

  constructor(private events: MicEvents) {}

  async init(settings: Settings) {
    if (this.vad) return;
    this.padMs = settings.vadPreSpeechPadMs;
    // Absolute origin URLs keep Vite from rewriting public/onnx and public/vad
    // dynamic imports into the module graph (those files must be fetched as static assets).
    const origin = window.location.origin;
    this.vad = await MicVAD.new({
      model: 'v5',
      baseAssetPath: `${origin}/vad/`,
      onnxWASMBasePath: `${origin}/onnx/`,
      positiveSpeechThreshold: settings.vadPositiveThreshold,
      negativeSpeechThreshold: settings.vadNegativeThreshold,
      redemptionMs: settings.vadSilenceMs,
      minSpeechMs: settings.vadMinSpeechMs,
      preSpeechPadMs: settings.vadPreSpeechPadMs,
      submitUserSpeechOnPause: false,
      startOnLoad: false,
      getStream: () =>
        navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
        }),
      onFrameProcessed: (probs, frame) => {
        const copy = new Float32Array(frame);
        this.pushRing(copy);
        const level = rms(copy);
        this.events.onFrame(probs.isSpeech, level, copy);
        if (this.mode === 'ptt' && this.ptt) this.pttFrames.push(copy);
      },
      onSpeechStart: () => {
        if (this.mode !== 'duplex') return;
        this.speechStartTs = Date.now();
        // Freeze lead-in now: the ring still holds pre-onset audio; soon it will be speech.
        this.leadSnapshot = this.ringTail(this.padSamples());
        this.events.onSpeechStart(this.speechStartTs);
      },
      onSpeechEnd: (audio) => {
        if (this.mode !== 'duplex') return;
        const merged = this.mergeLeadIn(audio);
        this.leadSnapshot = null;
        this.events.onSpeechEnd(merged, this.speechStartTs || Date.now() - (merged.length / SAMPLE_RATE) * 1000, Date.now());
      },
      onVADMisfire: () => {
        this.leadSnapshot = null;
        if (this.mode === 'duplex') this.events.onMisfire();
      },
    });
    this.ready = true;
  }

  applySettings(settings: Settings) {
    this.padMs = settings.vadPreSpeechPadMs;
    this.vad?.setOptions({
      positiveSpeechThreshold: settings.vadPositiveThreshold,
      negativeSpeechThreshold: settings.vadNegativeThreshold,
      redemptionMs: settings.vadSilenceMs,
      minSpeechMs: settings.vadMinSpeechMs,
      preSpeechPadMs: settings.vadPreSpeechPadMs,
    });
  }

  /** Temporarily raise the positive threshold while the assistant speaks (echo robustness). */
  setPlaybackBias(active: boolean, settings: Settings) {
    // Keep bias mild: too large a bump blocks soft-spoken barge-in during TTS.
    this.vad?.setOptions({
      positiveSpeechThreshold: active ? Math.min(0.95, settings.vadPositiveThreshold + 0.05) : settings.vadPositiveThreshold,
      negativeSpeechThreshold: active ? Math.min(0.9, settings.vadNegativeThreshold + 0.04) : settings.vadNegativeThreshold,
    });
  }

  async setMode(mode: 'duplex' | 'ptt' | 'off') {
    this.mode = mode;
    if (!this.vad) return;
    if (mode === 'off') await this.vad.pause();
    else await this.vad.start();
  }

  pttDown() {
    if (this.mode !== 'ptt' || this.ptt) return;
    this.ptt = true;
    this.pttFrames = [];
    this.pttStart = Date.now();
    this.events.onSpeechStart(this.pttStart);
  }

  pttUp() {
    if (this.mode !== 'ptt' || !this.ptt) return;
    this.ptt = false;
    const audio = concatFloat32(this.pttFrames);
    this.pttFrames = [];
    if (audio.length < SAMPLE_RATE * 0.2) {
      this.events.onMisfire();
      return;
    }
    this.events.onSpeechEnd(audio, this.pttStart, Date.now());
  }

  get pttHeld() {
    return this.ptt;
  }

  /** Snapshot the current mic ring (for mid-utterance streaming STT). */
  ringSnapshot(maxMs = 2500): Float32Array {
    const n = Math.min(this.ringSamples, Math.round((maxMs / 1000) * SAMPLE_RATE));
    return this.ringTail(n);
  }

  async destroy() {
    await this.vad?.destroy();
    this.vad = null;
    this.frameRing = [];
    this.ringSamples = 0;
    this.leadSnapshot = null;
    this.ready = false;
  }

  private padSamples(): number {
    return Math.max(0, Math.round((Math.max(this.padMs, 800) / 1000) * SAMPLE_RATE));
  }

  private pushRing(frame: Float32Array) {
    this.frameRing.push(frame);
    this.ringSamples += frame.length;
    const cap = Math.ceil((RING_CAP_MS / 1000) * SAMPLE_RATE);
    while (this.ringSamples > cap && this.frameRing.length > 1) {
      const drop = this.frameRing.shift()!;
      this.ringSamples -= drop.length;
    }
  }

  /**
   * Ensure STT gets ~padMs of audio before the VAD-detected onset. If the worklet already
   * prepended that pad, the snapshot is a prefix of `audio` and we return audio unchanged.
   */
  private mergeLeadIn(audio: Float32Array): Float32Array {
    const lead = this.leadSnapshot;
    this.leadSnapshot = null;
    if (!lead || lead.length === 0) return audio;
    if (isPrefixMatch(lead, audio)) return audio;
    return trimLeadingSilence(concatFloat32([lead, audio]));
  }

  private ringTail(sampleCount: number): Float32Array {
    if (sampleCount <= 0 || this.ringSamples === 0) return new Float32Array(0);
    const n = Math.min(sampleCount, this.ringSamples);
    const out = new Float32Array(n);
    let remaining = n;
    for (let i = this.frameRing.length - 1; i >= 0 && remaining > 0; i--) {
      const f = this.frameRing[i];
      const take = Math.min(f.length, remaining);
      out.set(f.subarray(f.length - take), remaining - take);
      remaining -= take;
    }
    return out;
  }
}

/** True when the end of `lead` matches the start of `audio` (library already included the pad). */
function isPrefixMatch(lead: Float32Array, audio: Float32Array): boolean {
  if (lead.length === 0) return true;
  if (audio.length < 160) return false;
  // Compare the last ~30 ms of the snapshot to the start of VAD audio (shared pad frames).
  const n = Math.min(480, lead.length, audio.length);
  const off = lead.length - n;
  let matches = 0;
  for (let i = 0; i < n; i++) {
    if (lead[off + i] === audio[i]) matches++;
  }
  // Allow a little drift from float copies; require strong overlap.
  return matches / n > 0.95;
}

/** Drop near-silent samples at the start so double-padding does not waste Whisper context. */
function trimLeadingSilence(audio: Float32Array, floor = 0.008): Float32Array {
  const frame = 160; // 10 ms
  let i = 0;
  while (i + frame <= audio.length) {
    let s = 0;
    for (let j = 0; j < frame; j++) s += audio[i + j] * audio[i + j];
    if (Math.sqrt(s / frame) >= floor) break;
    i += frame;
  }
  // Keep a tiny bit of silence before the first energy so Whisper does not clip the attack.
  const keep = Math.max(0, i - frame * 2);
  return keep === 0 ? audio : audio.subarray(keep);
}
